import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import { Search } from "@mui/icons-material";
import { HeaderCategory } from "./main-layout.types";

interface MobileSearchBarProps {
  t: (key: string, options?: Record<string, unknown>) => string;
  theme: Theme;
  isRTL: boolean;
  search: string;
  category: string;
  condition: string;
  priceRange: string;
  categories: HeaderCategory[];
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFilterChange: (
    key: "category" | "condition" | "priceRange",
    value: string,
  ) => void;
  onCategoryNavigate: (value: string) => void;
}

const MobileSearchBar: React.FC<MobileSearchBarProps> = ({
  t,
  theme,
  isRTL,
  search,
  category,
  condition,
  priceRange,
  categories,
  onSearchChange,
  onFilterChange,
  onCategoryNavigate,
}) => {
  return (
    <Box
      sx={{
        display: { xs: "block", md: "none" },
        px: 2,
        py: 1,
        borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
      }}
    >
      <Stack spacing={1}>
        <TextField
          fullWidth
          size="small"
          placeholder={t("common.searchPlaceholder")}
          value={search}
          onChange={onSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position={isRTL ? "end" : "start"}>
                <Search sx={{ color: "#00d4ff" }} />
              </InputAdornment>
            ),
            sx: {
              borderRadius: "8px",
              "& input": {
                color: theme.palette.text.primary,
                py: 0.5,
                fontSize: "0.875rem",
                textAlign: isRTL ? "right" : "left",
              },
            },
          }}
          sx={{
            flex: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 10,
            },
          }}
        />
        <Stack direction="row" spacing={1}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <Select
              value={category}
              displayEmpty
              onChange={(e) => {
                const value = e.target.value as string;
                onFilterChange("category", value);
                onCategoryNavigate(value);
              }}
              sx={{
                borderRadius: 10,
                color: theme.palette.text.primary,
                fontSize: "0.75rem",
                "& .MuiSelect-select": { py: 0.5 },
              }}
            >
              <MenuItem value="">
                <em>{t("products.categories")}</em>
              </MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.key} value={cat.key}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ flex: 1 }}>
            <Select
              value={condition}
              displayEmpty
              onChange={(e) =>
                onFilterChange("condition", e.target.value as string)
              }
              sx={{
                borderRadius: 10,
                color: theme.palette.text.primary,
                fontSize: "0.75rem",
                "& .MuiSelect-select": { py: 0.5 },
              }}
            >
              <MenuItem value="">
                <em>
                  {t("products.condition", { defaultValue: "Condition" })}
                </em>
              </MenuItem>
              <MenuItem value="excellent">{t("products.excellent")}</MenuItem>
              <MenuItem value="good">{t("products.good")}</MenuItem>
              <MenuItem value="fair">{t("products.fair")}</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ flex: 1 }}>
            <Select
              value={priceRange}
              displayEmpty
              onChange={(e) =>
                onFilterChange("priceRange", e.target.value as string)
              }
              sx={{
                borderRadius: 10,
                color: theme.palette.text.primary,
                fontSize: "0.75rem",
                "& .MuiSelect-select": { py: 0.5 },
              }}
            >
              <MenuItem value="">
                <em>{t("products.price")}</em>
              </MenuItem>
              <MenuItem value="0-5000">{t("products.under5000")}</MenuItem>
              <MenuItem value="5000-20000">
                {t("products.5000to20000")}
              </MenuItem>
              <MenuItem value="20000-50000">
                {t("products.20000to50000")}
              </MenuItem>
              <MenuItem value="50000-100000">
                {t("products.50000to100000")}
              </MenuItem>
              <MenuItem value="100000-">{t("products.over100000")}</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Stack>
    </Box>
  );
};

export default MobileSearchBar;